import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './HomeOwnerPricing.module.css';
import { homeownerBillingService } from '../../services/api';

const HomeOwnerPricing = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [proratedPrices, setProratedPrices] = useState({});
  
  // Get authentication status from localStorage
  const isAuthenticated = !!localStorage.getItem('token');

  // Add-on configuration (matches PlanUsageModal)
  const ADDON_CONFIG = {
    'extra-comparisons': {
      name: 'Extra Comparisons Pack',
      price: 15.00,
      pricePerUnit: 3.00,
      quantity: 5,
      description: 'Add 5 additional project comparisons'
    }
  };

  // Check if user can purchase addons
  const canPurchaseAddons = () => {
    return isAuthenticated && currentSubscription && currentSubscription.plan_name;
  };

  // Get plan display name for addon section
  const getPlanDisplayName = (planName) => {
    if (!planName) return 'Free Tier';
    
    const plan = planName.toLowerCase();
    switch (plan) {
      case 'essential':
        return 'Essential Plan';
      case 'complete':
        return 'Complete Plan';
      case 'expert':
        return 'Expert Plan';
      default:
        return `${planName} Plan`;
    }
  };

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchCurrentSubscription();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentSubscription && products.length > 0) {
      calculateProratedPrices();
    }
  }, [currentSubscription, products]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await homeownerBillingService.getPlans();
      console.log('API Response:', response); // Debug log
      
      // Check if response has the expected structure
      let plans;
      if (response && response.data && response.data.data) {
        // If response has nested data structure
        plans = response.data.data;
      } else if (response && response.data) {
        // If response.data is the plans array directly
        plans = response.data;
      } else {
        // If response is the plans array directly
        plans = response;
      }
      
      // Ensure plans is an array
      if (!Array.isArray(plans)) {
        console.error('Plans is not an array:', plans);
        throw new Error('Invalid response format');
      }
      
      // Transform backend data to frontend format
      const transformedPlans = plans.map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: parseFloat(plan.price),
        originalPrice: calculateOriginalPrice(parseFloat(plan.price)), // Business logic for discounts
        features: plan.features || [],
        popular: plan.name === 'Complete', // Business logic
        buttonText: `Get ${plan.name}`,
        billing_cycle: plan.billing_cycle,
        stripe_price_id: plan.stripe_price_id
      }));
      
      setProducts(transformedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load pricing plans');
      // Fallback to static plans if API fails
      setProducts(getStaticPlans());
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await homeownerBillingService.getSubscription();
      if (response.data?.data) {
        setCurrentSubscription(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      // Don't show error to user, just proceed without proration
    }
  };

  const calculateProratedPrices = async () => {
    if (!currentSubscription) return;
    
    const proratedData = {};
    
    // Calculate proration for each plan
    for (const product of products) {
      try {
        const response = await homeownerBillingService.getProrationPreview(product.id, product.name);
        if (response.data?.data?.isUpgrade && response.data.data.proration) {
          proratedData[product.id] = {
            ...response.data.data.proration,
            isUpgrade: true
          };
        }
      } catch (error) {
        console.error(`Error calculating proration for plan ${product.name}:`, error);
      }
    }
    
    setProratedPrices(proratedData);
  };

  const calculateOriginalPrice = (currentPrice) => {
    // Business logic: show original price as 2x current price to show discount
    return currentPrice * 2;
  };

  const getStaticPlans = () => {
    // Fallback static plans in case backend is unavailable
    return [
      {
        id: 1,
        name: 'Essential',
        description: 'Perfect for homeowners who want solid analysis with AI assistance',
        price: 19.99,
        originalPrice: 39.99,
        features: [
          'AI-powered analysis',
          'Compare up to 3 contractors per project',
          'Up to 5 project comparisons',
          'Contractor behavior analysis questions',
          'Downloadable PDF report',
          'Summary of hidden costs',
          'Basic risk analysis for homeowner & project',
          'Basic cost breakdown',
          'Scoring for each contractor',
          'User preferences questions'
        ],
        popular: false,
        buttonText: "Get Essential"
      },
      {
        id: 2,
        name: 'Complete',
        description: 'For serious planners who want detailed insights and comprehensive comparisons',
        price: 39.99,
        originalPrice: 79.99,
        features: [
          'Everything in Essential',
          'Compare up to 5 contractors per project',
          'Up to 10 project comparisons',
          'Basic timeline evaluation',
          'Full comparison analysis',
          'Detailed cost breakdown',
          'Detailed allowance breakdown',
          'Missing elements detection',
          'Risk for homeowner & project',
          'Priority email support',
          'Custom contractor questions',
          'Detailed info on each contractor',
          'File storage (1GB)'
        ],
        popular: true,
        buttonText: "Get Complete"
      },
      {
        id: 3,
        name: 'Expert',
        description: 'For professionals and homeowners handling large or complex projects',
        price: 79.99,
        originalPrice: 149.99,
        features: [
          'Everything in Complete',
          'Unlimited contractors per project',
          'Up to 20 project comparisons',
          'Negotiation strategy guide',
          'Priority phone support',
          'Project management tools',
          'Full file storage access',
          'AI-powered proposal generation',
          'Advanced risk modeling & timeline simulation',
          'File storage (5GB)'
        ],
        popular: false,
        buttonText: "Get Expert"
      }
    ];
  };

  const handlePurchase = (product) => {
    // Check if this is the current plan
    if (currentSubscription && currentSubscription.plan_name === product.name) {
      return; // Don't allow clicking on current plan
    }

    if (!isAuthenticated) {
      navigate('/homeowner/login', { state: { from: '/homeowner/upgrade' } });
      return;
    }

    // Calculate the final price based on billing cycle and proration
    let finalPrice = isAnnual ? Math.round(product.price * 12 * 0.8) : product.price;
    
    // Apply proration if this is an upgrade
    if (proratedPrices[product.id]) {
      finalPrice = proratedPrices[product.id].proratedAmount;
    }

    const productWithBilling = {
      ...product,
      finalPrice,
      billingCycle: isAnnual ? 'yearly' : 'monthly',
      isUpgrade: !!proratedPrices[product.id],
      proratedDetails: proratedPrices[product.id] || null
    };

    // Navigate to checkout with product details
    navigate('/homeowner/checkout', {
      state: { selectedPlan: productWithBilling }
    });
  };

  const calculatePrice = (basePrice) => {
    if (isAnnual) {
      return Math.round(basePrice * 12 * 0.8); // 20% discount for annual
    }
    return basePrice;
  };


  // Loading state display
  if (loading) {
    return (
      <div className={styles.pricingContainer}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h1>Choose Your Analysis Package</h1>
            <p>Loading pricing plans...</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Loading plans...</div>
        </div>
      </div>
    );
  }

  // Error state display - show error but continue with fallback plans
  if (error && products.length === 0) {
    return (
      <div className={styles.pricingContainer}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h1>Choose Your Analysis Package</h1>
            <p style={{ color: 'red' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pricingContainer}>
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1>Choose Your Analysis Package</h1>
          <p>Limited time offer - Save up to 50% on detailed contractor analysis</p>
          
          {/* <div className={styles.pricingToggle}>
            <span className={!isAnnual ? styles.active : ''}>Monthly</span>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={() => setIsAnnual(!isAnnual)}
              />
              <span className={styles.slider}></span>
            </label>
            <span className={isAnnual ? styles.active : ''}>
              Annual
              <span className={styles.discount}>Save 20%</span>
            </span>
          </div> */}
        </div>
      </div>

      <div className={styles.pricingGrid}>
        {products.map((product) => (
          <div 
            key={product.id} 
            className={`${styles.pricingCard} ${product.popular ? styles.popular : ''}`}
          >
            {product.popular && (
              <div className={styles.popularBadge}>Most Popular</div>
            )}
            <div className={styles.planHeader}>
              <h3>{product.name}</h3>
              <p className={styles.description}>{product.description}</p>
            </div>
            <div className={styles.price}>
              {product.originalPrice && !proratedPrices[product.id] && (
                <div className={styles.originalPrice}>${product.originalPrice}</div>
              )}
              <div className={styles.currentPriceWrapper}>
              <span className={styles.currency}>$</span>
              <span className={styles.amount}>
                {proratedPrices[product.id] ? 
                  proratedPrices[product.id].proratedAmount.toFixed(2) : 
                  calculatePrice(product.price)
                }
              </span>
              <span className={styles.period}>
                {proratedPrices[product.id] 
                  ? ' this month' 
                  : `/${isAnnual ? 'year' : 'month'}`
                }
              </span>
              </div>
              {proratedPrices[product.id] && (
                <div className={styles.proratedInfo}>
                  <div className={styles.originalPlanPrice}>
                    Regular price: ${calculatePrice(product.price)}
                  </div>
                  <div className={styles.proratedSavings}>
                    Prorated credit: ${proratedPrices[product.id].creditAmount.toFixed(2)}
                  </div>
                  <div className={styles.proratedExplanation}>
                    You'll get credit for {proratedPrices[product.id].remainingDays} unused days
                  </div>
                  <div className={styles.nextMonthPrice}>
                    Next month: ${calculatePrice(product.price)}/{isAnnual ? 'year' : 'month'}
                  </div>
                </div>
              )}
              {product.originalPrice && !proratedPrices[product.id] && (
                <div className={styles.savings}>
                  Save ${(product.originalPrice - product.price).toFixed(2)}
                </div>
              )}
            </div>
            <ul className={styles.features}>
              {product.features.map((feature, index) => (
                <li key={index}>
                  <i className="fas fa-check-circle"></i>
                  {feature}
                </li>
              ))}
            </ul>
            <button 
              className={styles.pricingButton}
              onClick={() => handlePurchase(product)}
              disabled={currentSubscription && currentSubscription.plan_name === product.name}
            >
              {currentSubscription && currentSubscription.plan_name === product.name 
                ? 'Current Plan' 
                : proratedPrices[product.id] 
                  ? `Upgrade to ${product.name}` 
                  : product.buttonText
              }
            </button>
          </div>
        ))}
      </div>

      {/* Extra Comparisons Add-on Section - Only show for paid subscribers */}
      {canPurchaseAddons() && (
        <div className={styles.extraComparisonsSection}>
          <div className={styles.extraComparisonsContent}>
            <div className={styles.extraComparisonsHeader}>
              <h2>
                <i className="fas fa-plus-circle"></i>
                Need More Project Comparisons?
              </h2>
              <p>Get additional comparisons when you need them most</p>
            </div>

            <div className={styles.addonEnabledState}>
              <div className={styles.addonInfo}>
                <p className={styles.currentPlanInfo}>
                  <i className="fas fa-check-circle"></i>
                  You're currently on the <strong>{getPlanDisplayName(currentSubscription.plan_name)}</strong>
                </p>
                <p className={styles.addonDescription}>
                  You can add 5 more comparisons whenever you need them!
                </p>
              </div>
              
              <div className={styles.addOnCard}>
                <div className={styles.addOnHeader}>
                  <div className={styles.addOnIcon}>
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className={styles.addOnDetails}>
                    <h4>Extra Comparisons Pack</h4>
                    <p>Add 5 additional project comparisons</p>
                  </div>
                </div>
                
                <div className={styles.addOnPricing}>
                  <div className={styles.addOnPrice}>
                    <span className={styles.currency}>$</span>
                    <span className={styles.amount}>{ADDON_CONFIG['extra-comparisons'].price}</span>
                    <span className={styles.period}>one-time</span>
                  </div>
                  <div className={styles.pricePerComparison}>
                    ${ADDON_CONFIG['extra-comparisons'].pricePerUnit.toFixed(2)} per comparison
                  </div>
                </div>
                
                <button 
                  className={styles.buyNowButton}
                  onClick={() => {
                    // Navigate to checkout with add-on data using correct route
                    navigate('/homeowner/checkout?' + new URLSearchParams({
                        type: 'addon',
                        product: 'extra-comparisons',
                        quantity: '5',
                        planId: currentSubscription?.plan_id || '',
                        planName: currentSubscription?.plan_name || ''
                    }).toString());
                  }}
                >
                  <i className="fas fa-shopping-cart"></i>
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.howItWorks}>
        <h2>How It Works</h2>
        <div className={styles.stepsContainer}>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-upload"></i>
            </div>
            <h3>Upload Your Quotes</h3>
            <p>Upload contractor proposals you've received for your renovation project.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-robot"></i>
            </div>
            <h3>AI Analysis</h3>
            <p>Our system analyzes contracts, identifies key differences and potential issues.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-file-alt"></i>
            </div>
            <h3>Get Your Report</h3>
            <p>Receive a comprehensive report with insights and side-by-side comparisons.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-handshake"></i>
            </div>
            <h3>Make Confident Decisions</h3>
            <p>Choose the right contractor with complete peace of mind.</p>
          </div>
        </div>
      </div>

      <div className={styles.testimonials}>
        <h2>What Homeowners Say</h2>
        <div className={styles.testimonialsGrid}>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The comparison report saved me over $5,000 by identifying unnecessary costs in one of my quotes.</p>
              <div className={styles.author}>— Jennifer K., Kitchen Remodel</div>
            </div>
          </div>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The clarification questions guided my conversations with contractors and helped me avoid potential misunderstandings.</p>
              <div className={styles.author}>— Michael T., Bathroom Renovation</div>
            </div>
          </div>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The complete bundle was worth every penny. I felt so much more confident negotiating with contractors knowing I had expert analysis.</p>
              <div className={styles.author}>— Sarah B., Home Addition</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.faqSection}>
        <h2>Frequently Asked Questions</h2>
        <div className={styles.faqGrid}>
          <div className={styles.faqItem}>
            <h3>How long does the analysis take?</h3>
            <p>Most reports are completed within 24 hours of submission. For complex projects with multiple quotes, it may take up to 48 hours.</p>
          </div>
          <div className={styles.faqItem}>
          <h3>Can I cancel my subscription anytime?</h3>
          <p>Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.</p>
          </div>
          <div className={styles.faqItem}>
            <h3>What file formats do you accept?</h3>
            <p>We accept PDF, DOC, DOCX, JPG, and PNG files. If your quote is in another format, please contact support.</p>
          </div>
          <div className={styles.faqItem}>
            <h3>What if I need help understanding my report?</h3>
            <p>Our support team is available to help you understand any part of your report. Premium bundle customers receive priority support.</p>
          </div>
        </div>
      </div>

      <div className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready to Upgrade Your Renovation Journey?</h2>
          <p>Make informed decisions and get the best value for your home improvement project.</p>
          <div className={styles.ctaButtons}>
            <button onClick={() => handlePurchase(products[2])} className={styles.ctaButton}>
              Get Complete Bundle
            </button>
            <Link to="/homeowner/support" className={styles.ctaButtonSecondary}>
              Have Questions?
            </Link>
          </div>
          <div className={styles.paymentIcons}>
            <i className="fab fa-cc-visa"></i>
            <i className="fab fa-cc-mastercard"></i>
            <i className="fab fa-cc-amex"></i>
            <i className="fab fa-cc-paypal"></i>
            <i className="fab fa-stripe"></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeOwnerPricing; 